import { deleteCustomer, useCustomer } from '@commenda/api';
import { TransactionSourcePlatformText } from '@commenda/api/sales-tax/constants';
import { Button, Loading, useToggle } from '@commenda/kakashi';
import { TSetNotification } from '@commenda/types/src/random';
import { copyToClipboard } from '@commenda/utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { useRouter } from 'next/router';
import { type ReactNode, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { DataPoint } from '../../../../Corporation/common/DataPoint';
import SalesTaxNavBar from '../../../common/navbar/Navbar';
import { useNavigationConfigForExemptCustomerForm } from '../utils/navigation';
import CustomerHeader from './common/CustomerHeader';
import ExemptionList from './ExemptionList';
import CustomerRemoveModal from './modals/CustomerRemoveModal';
import { CUSTOMER_FORM_SCHEMA, CustomerFormSchema } from './Schemas';

const getCustomerFormInstance = (schema: typeof CUSTOMER_FORM_SCHEMA) => {
  const customerForm = useForm<CustomerFormSchema>({
    resolver: zodResolver(schema()),
    defaultValues: {
      customerId: '',
      customerName: '',
      email: '',
    },
  });

  return customerForm;
};

const ExemptCustomerForm = ({
  corporationId,
  customerId,
  setNotification,
  hideNavbar = false,
  renderHeader,
}: {
  corporationId: number;
  customerId: string;
  setNotification: TSetNotification;
  hideNavbar?: boolean;
  /** When provided, replaces the default CustomerHeader. Receives action buttons as a ReactNode. */
  renderHeader?: (actions: ReactNode) => ReactNode;
}) => {
  const router = useRouter();
  const deleteCustomerConfirmationVisibility = useToggle();

  const { customer, error } = useCustomer({
    customerId,
    corporation_id: corporationId,
  });

  const { watch, reset } = getCustomerFormInstance(CUSTOMER_FORM_SCHEMA);

  useEffect(() => {
    if (customer) {
      reset({
        customerId: customer.id,
        customerName: customer.name,
        email: customer.email,
      });
    }
  }, [customer]);

  const onCopyValue = (value: string) => {
    copyToClipboard(value);
    setNotification({
      show: true,
      valid: true,
      title: 'Success',
      message: 'Value copied to clipboard',
    });
  };

  const onDeleteCustomer = async () => {
    const response = await deleteCustomer(customerId, corporationId);

    if (!response.success) {
      setNotification({
        show: true,
        valid: false,
        title: 'Error',
        message: 'Failed to delete customer.',
      });
      return;
    }

    setNotification({
      show: true,
      valid: true,
      title: 'Success',
      message: 'Customer deleted successfully.',
    });

    router.push(`/sales-tax/${corporationId}/exemptions`);
  };

  const navigationConfig = useNavigationConfigForExemptCustomerForm({
    customerName: customer?.name || 'Customer',
    corporationId: corporationId.toString(),
  });

  if (!customer) {
    if (error) console.error(error);

    return <Loading />;
  }

  const renderActionButtons = () => (
    <Button
      variant="destructive-secondary"
      size="sm"
      onClick={deleteCustomerConfirmationVisibility.show}
      type="button"
    >
      Delete
    </Button>
  );

  return (
    <div className="flex flex-col h-screen overflow-hidden">
      <SalesTaxNavBar items={navigationConfig} hidden={hideNavbar} />
      {renderHeader ? (
        renderHeader(
          <div className="flex items-center gap-3">{renderActionButtons()}</div>
        )
      ) : (
        <CustomerHeader
          customerName={watch('customerName')}
          isEditing={false}
          onCancel={() => {}}
          onSave={() => {}}
          onDelete={deleteCustomerConfirmationVisibility.show}
        />
      )}
      <div className="flex-1 overflow-auto">
        <div className="p-10 flex flex-col gap-14">
          <div className="flex flex-col gap-12 max-w-[600px]">
            <div className="flex flex-col gap-4">
              <div className="txt-extra-large-bold text-text-secondary">
                Customer Details
              </div>
              <div className="w-[800px] flex flex-col border border-border-primary rounded-md overflow-clip divide-y divide-border-primary">
                <DataPoint
                  title="Customer ID"
                  className="w-full"
                  value={watch('customerId')}
                  onCopy={() => onCopyValue(watch('customerId'))}
                />
                <DataPoint
                  title="Customer Name"
                  className="w-full"
                  value={watch('customerName')}
                  onCopy={() => onCopyValue(watch('customerName'))}
                />
                <DataPoint
                  title="Email"
                  className="w-full"
                  value={watch('email')}
                  onCopy={() => onCopyValue(watch('email'))}
                />
                <DataPoint
                  title="Business Identification Number"
                  className="w-full"
                  value={
                    customer.business_identification_number?.trim() || 'N/A'
                  }
                  onCopy={() =>
                    onCopyValue(
                      customer.business_identification_number?.trim() || ''
                    )
                  }
                />
                <DataPoint
                  title="Source Platform"
                  className="w-full"
                  value={
                    customer?.source_platform &&
                    TransactionSourcePlatformText[customer.source_platform]
                      ? TransactionSourcePlatformText[customer.source_platform]
                      : 'N/A'
                  }
                  onCopy={() =>
                    onCopyValue(
                      customer?.source_platform &&
                        TransactionSourcePlatformText[customer.source_platform]
                        ? TransactionSourcePlatformText[
                            customer.source_platform
                          ]
                        : ''
                    )
                  }
                />
                <DataPoint
                  title="Source Platform ID"
                  className="w-full"
                  value={customer?.source_platform_id || 'N/A'}
                  onCopy={() => onCopyValue(customer?.source_platform_id || '')}
                />
              </div>
            </div>
          </div>
          {customerId && corporationId && (
            <ExemptionList
              corporationId={corporationId}
              customerId={customerId}
              setNotification={setNotification}
            />
          )}
        </div>
      </div>

      <CustomerRemoveModal
        isOpen={deleteCustomerConfirmationVisibility.visible}
        onClose={deleteCustomerConfirmationVisibility.hide}
        onRemoveCustomer={onDeleteCustomer}
      />
    </div>
  );
};

export default ExemptCustomerForm;
